import { DateTime } from 'luxon';
import { check, memoize, type } from '@mews-framework/utils';
import { accessible, comparable, localizable, manipulable } from '../componentEnhancers';
import DateFactory from '../dates/date';
import { DateTimePart, ISOFormat, LocalizationMethod } from '../enums';
import { getDateString, toLuxon } from '../utils';
import { WallClockMonth } from '../wallClockMonths';
import WallClockDate from './wallClockDate';

const PARTS = [DateTimePart.Year, DateTimePart.Month, DateTimePart.Week, DateTimePart.Date, DateTimePart.Day] as const;

const FORMATS = [[LocalizationMethod.String, 'Date']] as const;

const toWallClockDateLuxon = memoize((date: WallClockDate) => {
    type.check(date, WallClockDate);
    return toLuxon(date.value, ISOFormat.Date);
});

export default function WallClockDateComponent() {
    return type.create(WallClockDateComponent, WallClockDateService);
}

export const WallClockDateService = {
    ...comparable(toWallClockDateLuxon),
    ...manipulable(WallClockDate, toWallClockDateLuxon, PARTS),
    ...accessible(toWallClockDateLuxon, PARTS),
    ...localizable(WallClockDate, toWallClockDateLuxon, FORMATS),
    parse: (dateString: string) => {
        check.string(dateString);

        const luxon = toLuxon(dateString, ISOFormat.Date);
        return WallClockDate(luxon);
    },

    today() {
        const luxon = DateTime.utc();
        return WallClockDate(luxon);
    },

    toWallClockMonth(date: WallClockDate) {
        return WallClockMonth(toWallClockDateLuxon(date));
    },

    fromJavascriptDate(javascriptDate: Date) {
        type.check(javascriptDate, Date);

        const luxon = toLuxon(getDateString(javascriptDate), ISOFormat.Date);
        return WallClockDate(luxon);
    },

    toJavascriptDate(date: WallClockDate) {
        const luxon = toWallClockDateLuxon(date);
        return luxon.toJSDate();
    },

    toTimezonedDate(wallClockDate: WallClockDate, timezone: string) {
        type.check(wallClockDate, WallClockDate);
        check.timezone(timezone);
        check.notUtcTimezone(timezone);

        return DateFactory(toLuxon(wallClockDate.value, ISOFormat.Date, timezone));
    },
};
